<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
    <v-card-title class="d-flex align-center justify-space-between py-3 px-4">
      <span class="d-flex align-center gap-1">
        Step 2: Confirm Expenses
        <!-- Tooltip — redesign only -->
        <LlTooltip v-if="redesignEnabled" max-width="260" text="Interest accrues on hard costs only, shown separately when an interest rate is set. Edit expenses on the Expenses page, then return to re-confirm.">
          <template #activator>
            <v-icon size="16" color="grey" class="cursor-pointer mb-1">mdi-information-outline</v-icon>
          </template>
        </LlTooltip>
      </span>
      <!-- Add Expense button — redesign + inline flag only -->
      <LlSecondaryButton
        v-if="expenseInlineEnabled"
        size="small"
        color="primary"
        prepend-icon="mdi-plus"
        @click="$emit('open-add-expense')"
      >
        Add Expense
      </LlSecondaryButton>
    </v-card-title>

    <v-card-text>

      <!-- Loading skeleton -->
      <v-skeleton-loader v-if="loading" type="table-row-divider@5" />

      <template v-else>

        <!-- ══════════════════════════════════════════════════
             OLD DESIGN  (redesignEnabled = false)
             Plain non-collapsible table, same as pre-SC-45761
             ══════════════════════════════════════════════════ -->
        <template v-if="!redesignEnabled">

          <!-- Green summary card — leads with Total Case Costs (the figure the table footer,
               explanatory text, and Confirm button all agree on), unbilled/billed as sub-lines
               (sc-46645). -->
          <v-sheet rounded="lg" class="pa-4 mb-4" color="green-lighten-5" border>
            <div class="text-h6 font-weight-bold mb-1">
              {{ isClientPaysArrangement ? 'Total Case Costs' : 'Total Expenses' }}:
              {{ formatCurrency(isClientPaysArrangement ? totalCaseCosts : totalExpenses) }}
            </div>
            <template v-if="isClientPaysArrangement && billedExpensesTotal > 0">
              <div class="text-body-2 text-grey-darken-1">
                Unbilled (confirming now): {{ formatCurrency(totalExpenses) }}
              </div>
              <div class="text-body-2 text-grey-darken-1">
                Previously billed: {{ formatCurrency(billedExpensesTotal) }}
              </div>
            </template>
            <a class="text-primary text-body-2 cursor-pointer" href="#" @click.prevent="$emit('view-expenses')">
              View or edit expenses for this matter &rarr;
            </a>
          </v-sheet>

          <!-- Interest As-Of Date picker — above the table, old design style -->
          <div v-if="showInterest" class="mb-3" style="width: 200px">
            <div class="d-flex align-center gap-1 mb-1">
              <span class="text-body-2 font-weight-bold">Interest As-Of Date</span>
              <LlTooltip text="The end date for interest accrual. Expenses dated after this date accrue 0 days of interest.">
                <template #activator>
                  <v-icon size="14" color="grey" class="cursor-pointer">mdi-information-outline</v-icon>
                </template>
              </LlTooltip>
            </div>
            <LlDatePicker
              :model-value="expenseFromDateModel"
              :show-button="false"
              :hide-details="true"
              label=""
              @update:model-value="onExpenseFromDateChange"
            />
          </div>

          <!-- No-date warning -->
          <div v-if="showInterest && noDateCount > 0" class="text-body-2 text-orange-darken-2 mb-3">
            {{ noDateCount }} expense{{ noDateCount > 1 ? 's' : '' }} excluded from interest calculation (no date)
          </div>
          <!-- After as-of date warning -->
          <div v-if="showInterest && afterAsOfCount > 0" class="text-body-2 text-orange-darken-2 mb-3">
            {{ afterAsOfCount }} expense{{ afterAsOfCount > 1 ? 's' : '' }} dated after the as-of date accrue 0 interest
          </div>

          <!-- Expense table -->
          <template v-if="tableExpenses.length > 0">
            <v-table density="compact" class="mb-2">
              <thead>
                <tr>
                  <th class="text-left">Description</th>
                  <th class="text-left">Date</th>
                  <th v-if="isClientPaysArrangement" class="text-left">Status</th>
                  <th class="text-right">Amount</th>
                  <th v-if="showInterest" class="text-right">Days</th>
                  <th v-if="showInterest" class="text-right">Interest</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="expense in paginatedExpenses" :key="expense.id">
                  <td>{{ expense.description || '—' }}</td>
                  <td>{{ expense.date ? formatLongDateString(expense.date) : '—' }}</td>
                  <td v-if="isClientPaysArrangement">
                    <v-chip size="x-small" :color="expense.billed ? 'grey' : 'success'" variant="tonal">
                      {{ expense.billed ? 'Billed' : 'Unbilled' }}
                    </v-chip>
                  </td>
                  <td class="text-right">{{ formatCurrency(expense.amount) }}</td>
                  <td v-if="showInterest" class="text-right">
                    <span v-if="!expense.date || !expenseFromDate" class="text-grey-darken-1">—</span>
                    <span v-else>{{ daysForExpense(expense) }}</span>
                  </td>
                  <td v-if="showInterest" class="text-right">
                    <span v-if="!expense.date" class="text-grey-darken-1">N/A</span>
                    <span v-else-if="!expenseFromDate" class="text-grey-darken-1">—</span>
                    <span v-else>{{ formatCurrency(interestForExpense(expense)) }}</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <!-- Confirmed: show all three rows -->
                <template v-if="showInterest && interestConfirmed && !interestNeedsReconfirm">
                  <tr class="text-body-2 bg-grey-lighten-5">
                    <td colspan="4">Total Expenses</td>
                    <td class="text-right">{{ formatCurrency(totalExpenses) }}</td>
                  </tr>
                  <tr class="text-body-2 bg-grey-lighten-5">
                    <td colspan="4">
                      Total Expense Interest ({{ companyExpenseInterestRate }}%{{ expenseFromDate ? ` through ${expenseFromDateDisplay}` : '' }})
                    </td>
                    <td class="text-right">{{ formatCurrency(totalInterest) }}</td>
                  </tr>
                  <tr class="font-weight-bold bg-grey-lighten-4">
                    <td colspan="4">Total with Interest</td>
                    <td class="text-right text-green-darken-2">
                      {{ formatCurrency(totalExpenses + totalInterest) }}
                    </td>
                  </tr>
                </template>
                <!-- Interest active but not yet confirmed: show expense total + interest total inline -->
                <tr v-else-if="showInterest" class="font-weight-bold bg-grey-lighten-4">
                  <td colspan="2">Total</td>
                  <td class="text-right">{{ formatCurrency(totalExpenses) }}</td>
                  <td />
                  <td class="text-right text-green-darken-2">
                    {{ expenseFromDate ? formatCurrency(totalInterest) : '—' }}
                  </td>
                </tr>
                <!-- No interest: simple total. ClientPays' combined table includes billed rows
                     too, so the footer total is Total Case Costs, and colspan grows by one for
                     the Status column (sc-46645). -->
                <tr v-else class="font-weight-bold bg-grey-lighten-4">
                  <td :colspan="isClientPaysArrangement ? 3 : 2">{{ isClientPaysArrangement ? 'Total Case Costs' : 'Total' }}</td>
                  <td class="text-right">{{ formatCurrency(isClientPaysArrangement ? totalCaseCosts : totalExpenses) }}</td>
                </tr>
              </tfoot>
            </v-table>
            <!-- Pagination — only when more than one page -->
            <div v-if="totalPages > 1" class="d-flex justify-center mt-2">
              <v-pagination v-model="currentPage" :length="totalPages" :total-visible="7" density="compact" />
            </div>
          </template>
          <div v-else class="py-4 text-body-2 text-grey text-center">
            {{ isClientPaysArrangement ? 'No expenses for this matter.' : 'No unbilled expenses for this matter.' }}
          </div>

          <!-- Confirmed interest card -->
          <div
            v-if="hasConfirmedActiveInterest"
            class="mt-2 mb-2"
          >
            <div
              class="d-flex align-center justify-space-between pa-3 rounded mb-1"
              style="background: #f0fdf4; border: 1px solid #86efac;"
            >
              <div class="d-flex align-center gap-2">
                <LlIcon icon="mdi-check-circle" color="success" size="18" />
                <div>
                  <div class="text-body-2 font-weight-bold text-green-darken-2">
                    Interest Confirmed — Expense Created
                  </div>
                  <div class="text-caption text-grey-darken-1">
                    {{ companyExpenseInterestRate }}% interest charged on advanced client costs
                  </div>
                </div>
              </div>
              <span class="text-body-2 font-weight-bold text-green-darken-2">
                {{ formatCurrency(confirmedTotalInterest) }}
              </span>
            </div>
            <a class="text-body-2 text-error cursor-pointer" href="#" @click.prevent="$emit('remove-interest')">
              Remove interest expense
            </a>
          </div>

          <!-- Interest confirmation -->
          <div v-if="showInterest && interestNeedsReconfirm" class="mb-4">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="d-flex align-center gap-1 text-body-1 font-weight-medium">
                Total Expense Interest ({{ companyExpenseInterestRate }}%):
                <LlTooltip text="Ensure your fee agreement authorizes interest on advanced expenses before including this line item.">
                  <template #activator>
                    <v-icon size="14" color="grey" class="cursor-pointer">mdi-information-outline</v-icon>
                  </template>
                </LlTooltip>
              </span>
              <span class="text-body-1 font-weight-bold text-green-darken-2">
                {{ formatCurrency(totalInterest) }}
              </span>
            </div>
            <LlSecondaryButton
              color="primary"
              :disabled="!expenseFromDate || totalInterest <= 0 || saving"
              :loading="saving"
              class="mb-1"
              @click="$emit('confirm-interest')"
            >
              {{ interestConfirmed ? `Interest Confirmed (${formatCurrency(totalInterest)})` : `Confirm Interest (${formatCurrency(totalInterest)})` }}
            </LlSecondaryButton>
            <div class="text-caption text-grey-darken-1">
              Confirming creates an expense: "{{ companyExpenseInterestRate }}% interest charged on advanced client costs"
            </div>
          </div>

          <!-- Fee method explanation -->
          <div class="mt-4 mb-2 text-body-2">
            <template v-if="expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED">
              <template v-if="feeMethod === FEE_METHOD.NET">
                <strong>Firm absorbs expenses (net):</strong> These costs
                ({{ formatCurrency(totalExpenses) }}) will reduce the fee base &mdash;
                the attorney fee is calculated on the settlement minus expenses.
                The client pays no expenses.
              </template>
              <template v-else>
                <strong>Firm absorbs expenses (gross):</strong> These costs
                ({{ formatCurrency(totalExpenses) }}) will be deducted from the gross
                attorney fee. The client pays no expenses.
              </template>
            </template>
            <template v-else-if="expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT">
              <template v-if="feeMethod === FEE_METHOD.NET">
                <strong>Net method:</strong> All case costs ({{ formatCurrency(totalCaseCosts) }})
                are deducted from the recovery <em>before</em> calculating the fee.
              </template>
              <template v-else>
                <strong>Gross method:</strong> The attorney fee is calculated on the full recovery
                amount. Case costs ({{ formatCurrency(totalCaseCosts) }})
                will be deducted from the client's share.
              </template>
            </template>
            <template v-else>
              <template v-if="feeMethod === FEE_METHOD.NET">
                <template v-if="hasConfirmedActiveInterest">
                  <strong>Net method:</strong> These costs ({{ formatCurrency(totalExpenses) }}) plus
                  confirmed interest ({{ formatCurrency(confirmedTotalInterest) }}) will be deducted
                  from the recovery amount <em>before</em> calculating the attorney fee.
                </template>
                <template v-else>
                  <strong>Net method:</strong> These costs ({{ formatCurrency(totalExpenses) }})
                  will be deducted from the recovery amount <em>before</em> calculating the attorney fee.
                </template>
              </template>
              <template v-else>
                <template v-if="hasConfirmedActiveInterest">
                  <strong>Gross method:</strong> The attorney fee will be calculated on the full
                  recovery amount. These costs ({{ formatCurrency(totalExpenses) }}) plus confirmed
                  interest ({{ formatCurrency(confirmedTotalInterest) }}) will then be deducted from
                  the client's remaining share.
                </template>
                <template v-else>
                  <strong>Gross method:</strong> The attorney fee will be calculated on the full
                  recovery amount. These costs ({{ formatCurrency(totalExpenses) }}) will then be
                  deducted from the client's remaining share.
                </template>
              </template>
            </template>
          </div>

          <!-- Edit link -->
          <div class="text-body-2 text-grey-darken-1 mb-4">
            <a class="text-primary cursor-pointer" href="#" @click.prevent="$emit('view-expenses')">
              Edit expenses on the Expenses page
            </a>, then return here to re-confirm.
          </div>

        </template>

        <!-- ══════════════════════════════════════════════════
             NEW DESIGN  (redesignEnabled = true)
             Collapsible card + QBO dots + totals footer, all
             collapsed by default so the page opens compact.
             ══════════════════════════════════════════════════ -->
        <template v-else>

          <!-- ── Collapsible expense list ─────────────────────────── -->
          <div class="expense-list-card mb-4">
            <!-- Card header -->
            <div class="expense-list-head d-flex align-center justify-space-between pa-3">
              <div
                class="d-flex align-center gap-3 cursor-pointer flex-grow-1"
                @click="expenseListOpen = !expenseListOpen"
              >
                <v-icon
                  size="18"
                  class="expense-list-chevron"
                  :class="{ 'expense-list-chevron--open': expenseListOpen }"
                >
                  mdi-chevron-right
                </v-icon>
                <span class="text-body-1 font-weight-bold">
                  {{ firmName ? `${firmName}'s expenses:` : 'Total Expenses:' }} {{ formatCurrency(isClientPaysArrangement ? totalCaseCosts : totalExpenses) }}
                  <span class="text-body-2 font-weight-regular text-grey-darken-1 ml-1">
                    ({{ tableExpenses.length }})
                  </span>
                  <span
                    v-if="showInterest && totalInterest > 0"
                    class="text-body-2 font-weight-regular text-grey-darken-1 ml-2"
                  >
                    &middot; Total Interest: {{ formatCurrency(totalInterest) }}
                  </span>
                </span>
              </div>
              <LlSecondaryButton
                v-if="tableExpenses.length > 0"
                size="small"
                @click.stop="exportExpensesCsv"
              >
                Export
                <v-icon size="18" class="ml-1">mdi-tray-arrow-down</v-icon>
              </LlSecondaryButton>
            </div>

            <!-- Collapsible body -->
            <div v-show="expenseListOpen">
              <!-- Empty state -->
              <div v-if="tableExpenses.length === 0" class="py-4 text-body-2 text-grey text-center">
                No expenses on this matter.
                <a v-if="expenseInlineEnabled" class="text-primary cursor-pointer ml-1" href="#" @click.stop.prevent="$emit('open-add-expense')">
                  Add one now.
                </a>
              </div>

              <template v-else>
                <v-table density="compact" class="expense-list-table">
                  <thead>
                    <tr>
                      <!-- green dot indicator column -->
                      <th style="width: 28px" />
                      <th
                        class="text-uppercase text-caption expense-col-sortable"
                        @click="toggleSort('date')"
                      >
                        Date
                        <v-icon size="12" class="expense-sort-icon" :class="{ 'expense-sort-icon--active': sortKey === 'date' }">
                          {{ sortKey === 'date' && sortDir === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                        </v-icon>
                      </th>
                      <th
                        class="text-uppercase text-caption expense-col-sortable"
                        @click="toggleSort('description')"
                      >
                        Description
                        <v-icon size="12" class="expense-sort-icon" :class="{ 'expense-sort-icon--active': sortKey === 'description' }">
                          {{ sortKey === 'description' && sortDir === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                        </v-icon>
                      </th>
                      <th v-if="showVendor" class="text-uppercase text-caption">Vendor</th>
                      <th v-if="isClientPaysArrangement" class="text-uppercase text-caption">Status</th>
                      <th
                        class="text-uppercase text-caption text-right expense-col-sortable"
                        @click="toggleSort('amount')"
                      >
                        Amount
                        <v-icon size="12" class="expense-sort-icon" :class="{ 'expense-sort-icon--active': sortKey === 'amount' }">
                          {{ sortKey === 'amount' && sortDir === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                        </v-icon>
                      </th>
                      <th v-if="showInterest" class="text-uppercase text-caption text-right">Interest</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="expense in paginatedExpenses"
                      :key="expense.id"
                      :class="{ 'expense-row--clickable': expenseInlineEnabled }"
                      :title="expenseInlineEnabled ? 'Click to edit this expense' : undefined"
                      @click="expenseInlineEnabled && $emit('open-edit-expense', expense.id)"
                    >
                      <!-- QuickBooks icon for QBO-synced expenses -->
                      <td style="width: 28px; padding-left: 10px; padding-right: 0">
                        <QboIcon v-if="expense.accounting" :size="14" title="Synced from QuickBooks" />
                      </td>
                      <td class="text-body-2">{{ expense.date ? formatLongDateString(expense.date) : '—' }}</td>
                      <td class="text-body-2">{{ expense.description || '—' }}</td>
                      <td v-if="showVendor" class="text-body-2 text-grey-darken-1">{{ expense.vendor?.displayName || expense.vendor?.name || '—' }}</td>
                      <td v-if="isClientPaysArrangement">
                        <v-chip size="x-small" :color="expense.billed ? 'grey' : 'success'" variant="tonal">
                          {{ expense.billed ? 'Billed' : 'Unbilled' }}
                        </v-chip>
                      </td>
                      <td class="text-body-2 text-right">{{ formatCurrency(expense.amount) }}</td>
                      <td v-if="showInterest" class="text-body-2 text-right">
                        <span v-if="!expense.date" class="text-grey-darken-1">N/A</span>
                        <span v-else-if="!expenseFromDate" class="text-grey-darken-1">—</span>
                        <span v-else>{{ formatCurrency(interestForExpense(expense)) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </v-table>

                <!-- Pagination — "Showing X–Y of Z  ← 1/2 →" -->
                <div
                  class="d-flex align-center justify-space-between px-3 py-2 text-body-2 text-grey-darken-1"
                  style="border-top: 1px solid #e8eaed"
                >
                  <span>Showing {{ paginationStart }}–{{ paginationEnd }} of {{ tableExpenses.length }}</span>
                  <span v-if="totalPages > 1" class="d-flex align-center gap-1">
                    <LlSecondaryButton
                      icon="mdi-chevron-left"
                      variant="text"
                      size="x-small"
                      aria-label="Previous page"
                      :disabled="currentPage <= 1"
                      @click="currentPage--"
                    />
                    <span class="expense-page-indicator">{{ currentPage }} / {{ totalPages }}</span>
                    <LlSecondaryButton
                      icon="mdi-chevron-right"
                      variant="text"
                      size="x-small"
                      aria-label="Next page"
                      :disabled="currentPage >= totalPages"
                      @click="currentPage++"
                    />
                  </span>
                </div>
              </template>

              <!-- ── Totals footer (part of the same card, no gap) ─────────────── -->
              <!-- Leads with Total Case Costs (the figure the table above, explanatory text,
                   and Confirm button all agree on), unbilled/billed as plain sub-rows (sc-46645). -->
              <div class="expense-list-foot">
                <div class="expense-totals-row" :class="{ 'expense-totals-row--total': isClientPaysArrangement }">
                  <span :class="isClientPaysArrangement ? 'text-body-1 font-weight-bold' : 'text-body-2'">
                    {{ isClientPaysArrangement ? 'Total Case Costs' : 'Total Expenses' }}
                  </span>
                  <span :class="isClientPaysArrangement ? 'text-body-1 font-weight-bold text-green-darken-2' : 'text-body-2'">
                    {{ formatCurrency(isClientPaysArrangement ? totalCaseCosts : totalExpenses) }}
                  </span>
                </div>
                <!-- ClientPays only: breakdown of the total above (sc-46645) -->
                <template v-if="isClientPaysArrangement && billedExpensesTotal > 0">
                  <div class="expense-totals-row">
                    <span class="text-body-2">Unbilled (confirming now)</span>
                    <span class="text-body-2">{{ formatCurrency(totalExpenses) }}</span>
                  </div>
                  <div class="expense-totals-row">
                    <span class="text-body-2">Previously billed</span>
                    <span class="text-body-2">{{ formatCurrency(billedExpensesTotal) }}</span>
                  </div>
                </template>
                <!-- Interest totals only shown once interest is confirmed (BR: don't show unconfirmed interest in totals) -->
                <template v-if="showInterest && expenseFromDate && interestConfirmed && !interestNeedsReconfirm">
                  <div class="expense-totals-row">
                    <span class="text-body-2">
                      Total Expense Interest ({{ companyExpenseInterestRate }}% through {{ expenseFromDateDisplay }})
                    </span>
                    <span class="text-body-2">{{ formatCurrency(totalInterest) }}</span>
                  </div>
                  <div class="expense-totals-row expense-totals-row--total">
                    <span class="text-body-1 font-weight-bold">Total with Interest</span>
                    <span class="text-body-1 font-weight-bold text-green-darken-2">
                      {{ formatCurrency(totalExpenses + totalInterest) }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Fee method explanation -->
          <div class="mt-2 mb-3 text-body-2">
            <template v-if="expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED">
              <template v-if="feeMethod === FEE_METHOD.NET">
                <strong>Firm absorbs expenses (net):</strong> These costs
                ({{ formatCurrency(totalExpenses) }}) will reduce the fee base &mdash;
                the attorney fee is calculated on the settlement minus expenses.
                The client pays no expenses.
              </template>
              <template v-else>
                <strong>Firm absorbs expenses (gross):</strong> These costs
                ({{ formatCurrency(totalExpenses) }}) will be deducted from the gross
                attorney fee. The client pays no expenses.
              </template>
            </template>
            <template v-else-if="expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT">
              <template v-if="feeMethod === FEE_METHOD.NET">
                <strong>Net method:</strong> All case costs ({{ formatCurrency(totalCaseCosts) }})
                are deducted from the recovery <em>before</em> calculating the fee.
              </template>
              <template v-else>
                <strong>Gross method:</strong> The attorney fee is calculated on the full recovery
                amount. Case costs ({{ formatCurrency(totalCaseCosts) }})
                will be deducted from the client's share.
              </template>
            </template>
            <template v-else>
              <template v-if="feeMethod === FEE_METHOD.NET">
                <template v-if="hasConfirmedActiveInterest">
                  <strong>Net method:</strong> These costs ({{ formatCurrency(totalExpenses) }}) plus
                  confirmed interest ({{ formatCurrency(confirmedTotalInterest) }}) will be deducted
                  from the recovery amount <em>before</em> calculating the attorney fee.
                </template>
                <template v-else>
                  <strong>Net method:</strong> These costs ({{ formatCurrency(totalExpenses) }})
                  will be deducted from the recovery amount <em>before</em> calculating the attorney fee.
                </template>
              </template>
              <template v-else>
                <template v-if="hasConfirmedActiveInterest">
                  <strong>Gross method:</strong> The attorney fee will be calculated on the full
                  recovery amount. These costs ({{ formatCurrency(totalExpenses) }}) plus confirmed
                  interest ({{ formatCurrency(confirmedTotalInterest) }}) will then be deducted from
                  the client's remaining share.
                </template>
                <template v-else>
                  <strong>Gross method:</strong> The attorney fee will be calculated on the full
                  recovery amount. These costs ({{ formatCurrency(totalExpenses) }}) will then be
                  deducted from the client's remaining share.
                </template>
              </template>
            </template>
          </div>

          <!-- ── Interest section (collapsible) ─────────────────── -->
          <div v-if="showInterest" class="interest-section mb-4">
            <div
              class="d-flex align-center gap-2 cursor-pointer mb-0"
              @click="interestOpen = !interestOpen"
            >
              <v-icon
                size="16"
                class="expense-list-chevron"
                :class="{ 'expense-list-chevron--open': interestOpen }"
              >
                mdi-chevron-right
              </v-icon>
              <span class="text-body-2 font-weight-bold">
                Expense Interest ({{ companyExpenseInterestRate }}%)
              </span>
              <span v-if="hasConfirmedActiveInterest" class="ml-1">
                <LlIcon icon="mdi-check-circle" size="14" color="success" />
                <span class="text-caption text-green-darken-2 ml-1">Confirmed</span>
              </span>
            </div>

            <div v-show="interestOpen" class="pl-2">
              <div v-if="!expenseFromDate" class="text-body-2 text-grey-darken-1 mb-2 d-flex align-center gap-1">
                <v-icon size="14" color="grey">mdi-information-outline</v-icon>
                Set the Interest As-Of Date in <strong>Settings</strong> to calculate interest.
              </div>
              <div v-if="noDateCount > 0" class="text-body-2 text-orange-darken-2 mb-2">
                {{ noDateCount }} expense{{ noDateCount > 1 ? 's' : '' }} excluded from interest calculation (no date)
              </div>
              <div v-if="afterAsOfCount > 0" class="text-body-2 text-orange-darken-2 mb-2">
                {{ afterAsOfCount }} expense{{ afterAsOfCount > 1 ? 's' : '' }} dated after the as-of date accrue 0 interest
              </div>

              <!-- Confirmed interest card -->
              <div
                v-if="hasConfirmedActiveInterest"
                class="mt-2 mb-2"
              >
                <div
                  class="d-flex align-center justify-space-between pa-3 rounded mb-1"
                  style="background: #f0fdf4; border: 1px solid #86efac;"
                >
                  <div class="d-flex align-center gap-2">
                    <LlIcon icon="mdi-check-circle" color="success" size="18" />
                    <div>
                      <div class="text-body-2 font-weight-bold text-green-darken-2">
                        Interest Confirmed — Expense Created
                      </div>
                      <div class="text-caption text-grey-darken-1">
                        {{ companyExpenseInterestRate }}% interest charged on advanced client costs
                      </div>
                    </div>
                  </div>
                  <span class="text-body-2 font-weight-bold text-green-darken-2">
                    {{ formatCurrency(confirmedTotalInterest) }}
                  </span>
                </div>
                <a class="text-body-2 text-error cursor-pointer" href="#" @click.prevent="$emit('remove-interest')">
                  Remove interest expense
                </a>
              </div>

              <!-- Interest confirmation (needs confirm / re-confirm) -->
              <div v-if="interestNeedsReconfirm" class="mb-3">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="d-flex align-center gap-1 text-body-1 font-weight-medium">
                    Total Expense Interest ({{ companyExpenseInterestRate }}%):
                    <LlTooltip text="Ensure your fee agreement authorizes interest on advanced expenses before including this line item.">
                      <template #activator>
                        <v-icon size="14" color="grey" class="cursor-pointer">mdi-information-outline</v-icon>
                      </template>
                    </LlTooltip>
                  </span>
                  <span class="text-body-1 font-weight-bold text-green-darken-2">
                    {{ formatCurrency(totalInterest) }}
                  </span>
                </div>
                <LlSecondaryButton
                  color="primary"
                  :disabled="!expenseFromDate || totalInterest <= 0 || saving"
                  :loading="saving"
                  class="mb-1"
                  @click="$emit('confirm-interest')"
                >
                  {{ interestConfirmed ? `Interest Confirmed (${formatCurrency(totalInterest)})` : `Confirm Interest (${formatCurrency(totalInterest)})` }}
                </LlSecondaryButton>
                <div class="text-caption text-grey-darken-1">
                  Confirming creates an expense: "{{ companyExpenseInterestRate }}% interest charged on advanced client costs"
                </div>
              </div>
            </div>
          </div>

          <!-- Co-Counsel / Referral Expenses (P5.2) — at the very bottom, below
               the expense table and the Expense Interest section. Independent of
               both, so it stays visible regardless of whether either is collapsed. -->
          <div v-if="showCoCounselSection" class="co-counsel-section mb-4">
            <div class="px-3 pb-2">
              <div v-if="!coCounselSectionOpen" class="d-flex justify-end">
                <LlSecondaryButton
                  variant="text"
                  color="primary"
                  size="small"
                  class="co-counsel-toggle-link text-body-2 px-0"
                  @click="coCounselSectionOpen = true"
                >
                  Need to add co-counsel expenses?
                </LlSecondaryButton>
              </div>

              <div v-else class="co-counsel-panel rounded-lg pa-3 bg-[#f1faf4] border border-[#bfe0c6]">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-body-2 font-weight-bold text-green-darken-4">Co-Counsel / Referral Expenses</span>
                  <LlSecondaryButton
                    variant="text"
                    color="primary"
                    size="small"
                    class="co-counsel-toggle-link text-body-2 px-0"
                    @click="coCounselSectionOpen = false"
                  >
                    Hide
                  </LlSecondaryButton>
                </div>

                <!-- Case A: FirmAbsorbs — read-only notice, no inputs (UI-010) -->
                <LlWarningBanner v-if="isFirmAbsorbsArrangement" :visible="true">
                  Co-Counsel expenses are absorbed by the generating firm and are not itemized separately or shown on the settlement statement.
                </LlWarningBanner>

                <!-- Case B: all other arrangements — editable rows -->
                <template v-else>
                  <div
                    v-for="row in coCounselExpenses"
                    :key="row.id"
                    class="co-counsel-row d-flex align-center justify-space-between py-1"
                    :data-vendor-id="row.vendorId"
                  >
                    <div>
                      <span class="text-body-2 font-weight-bold text-green-darken-4 d-block">{{ row.vendorDisplayName }}</span>
                      <span class="text-caption text-grey d-block">{{ roleLabel(row.role) }}</span>
                    </div>
                    <div class="d-flex align-center gap-2">
                      <span v-if="row.role === 'coCounsel'" class="text-body-2">Expenses ($):</span>
                      <div class="w-[110px]">
                        <LlTextField
                          v-if="row.role === 'coCounsel'"
                          :model-value="row.amount"
                          type="number"
                          density="compact"
                          hide-details
                          step="0.01"
                          @update:model-value="(value) => onCoCounselAmountChange(row.vendorId, value)"
                        />
                        <span v-else class="text-body-2 text-grey-darken-1 d-block text-right">$0</span>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex align-center justify-space-between pt-2 mt-1 border-t border-[#bfe0c6]">
                    <span class="text-body-2 font-weight-bold text-green-darken-4">Co-Counsel Expense Total:</span>
                    <span class="text-body-2 font-weight-bold text-green-darken-4">{{ formatCurrency(coCounselExpenseTotal) }}</span>
                  </div>
                </template>
              </div>
            </div>

            <!-- Combined bottom-line total — firm expenses + co-counsel expenses. -->
            <div class="d-flex align-center justify-space-between px-3 pb-3">
              <span class="text-body-1 font-weight-bold">Total Expenses:</span>
              <span class="text-body-1 font-weight-bold text-green-darken-2">{{ formatCurrency(totalExpensesWithCoCounsel) }}</span>
            </div>
          </div>

        </template>
        <!-- ── end new design ── -->

        <!-- SHARED: Negative fee base warning -->
        <AlertBar
          v-if="hasNegativeFeeBase"
          type="error"
          class="mb-4"
        >
          <div class="font-weight-bold mb-1">Expenses exceed recovery</div>
          <div class="text-body-2">
            With the "Net of Expenses" fee method, included expenses
            ({{ formatCurrency(totalExpenses) }}) exceed the
            recovery amount. You cannot proceed until expenses are reduced
            or the fee method is changed to "Gross."
          </div>
        </AlertBar>
      </template>

      <!-- Navigation -->
      <div class="sett-step-footer">
        <LlSecondaryButton @click="$emit('go-back')">
          &larr; Back
        </LlSecondaryButton>
        <LlSecondaryButton
          color="primary"
          style="margin-left: auto"
          :disabled="hasNegativeFeeBase || loading || (showInterest && totalInterest > 0 && interestNeedsReconfirm)"
          :loading="saving"
          @click="$emit('confirm-expenses')"
        >
          <template v-if="redesignEnabled">Confirm Expenses &rarr;</template>
          <template v-else>Confirm Expenses ({{ confirmButtonLabel }}) &rarr;</template>
        </LlSecondaryButton>
      </div>

    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import {
  ref, computed, onMounted, watch,
} from 'vue';
import { format } from 'date-fns';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import AlertBar from '@/components/AlertBar.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import QboIcon from '@/components/QboIcon.vue';
import LlWarningBanner from '@/components/LlWarningBanner.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlIcon from '@/components/LlIcon.vue';
import { formatCurrency, formatLongDateString, parseLocalDate, yyyymmdddash } from '@/services/utils';
import LlDatePicker from '@/components/LlDatePicker.vue';
import { daysBetween, computeExpenseInterest } from '@/utils/expenseInterest';
import { useExportUtilities } from '@/composables/useExportUtilities';
import type { FeeMethod, ExpenseArrangement, MatterExpenseItem, CoCounselExpense } from '@/models/settlementTypes';
import { roleLabel } from '@/utils/roleLabels';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';

// pageSize.value is computed so the redesign (6/page) and old design (15/page) can coexist

const props = defineProps({
  expenses: { type: Array as () => MatterExpenseItem[], default: (): MatterExpenseItem[] => [] },
  totalExpenses: { type: Number, default: 0 },
  /** ClientPays only: previously billed case costs, outside this unbilled batch (sc-46645).
   *  0 for every other arrangement. */
  billedExpensesTotal: { type: Number, default: 0 },
  /** ClientPays only: the itemized previously-billed expenses backing billedExpensesTotal —
   *  merged into the table below with a Status column so users can see exactly what's billed
   *  vs unbilled, not just the two totals. Empty for every other arrangement. */
  billedExpenses: { type: Array as () => MatterExpenseItem[], default: (): MatterExpenseItem[] => [] },
  expenseArrangement: { type: String as () => ExpenseArrangement, default: EXPENSE_ARRANGEMENT.FIRM },
  feeMethod: { type: String as () => FeeMethod, default: FEE_METHOD.GROSS },
  hasNegativeFeeBase: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  companyExpenseInterestRate: { type: Number as () => number | null, default: null },
  noExpenseInterest: { type: Boolean, default: false },
  expenseFromDate: { type: String as () => string | null, default: null },
  totalInterest: { type: Number, default: 0 },
  interestConfirmed: { type: Boolean, default: false },
  confirmedExpenseFromDate: { type: String as () => string | null, default: null },
  confirmedTotalInterest: { type: Number, default: 0 },
  /** Feature flipper — when true shows "+ Add Expense" button and per-row edit icons.
   *  When false, the old "Edit on Expenses page" link is shown instead. */
  expenseInlineEnabled: { type: Boolean, default: false },
  /** Gate all SC-45761/SC-45762 redesign UI behind this flag.
   *  false = old design (tfoot totals, 15/page, Days col, amount in button).
   *  true  = new design (external totals, 6/page, green dot, no Days). */
  redesignEnabled: { type: Boolean, default: false },
  /** Law firm name — shown in the new-design card header as "{firmName}'s expenses:". */
  firmName: { type: String, default: '' },
  /** Mirror of me.features.importcsv — when true show the Vendor column in the redesign table. */
  showVendor: { type: Boolean, default: false },
  /** Mirror of features.coCounsel — gates the whole Co-Counsel Expenses sub-section. */
  coCounselEnabled: { type: Boolean, default: false },
  /** One row per matter recipient (co-counsel or referring attorney), joined with any
   *  confirmed Step 2 amount. Empty until the parent loads it via load-co-counsel-expenses. */
  coCounselExpenses: { type: Array as () => CoCounselExpense[], default: (): CoCounselExpense[] => [] },
});

const emit = defineEmits([
  'confirm-expenses',
  'confirm-interest',
  'go-back',
  'open-add-expense',
  'open-edit-expense',
  'remove-interest',
  /** Fallback: navigate to the standalone Expenses page (flag off). */
  'view-expenses',
  /** Old design only: user changed the interest as-of date inline on Step 2. */
  'update:expenseFromDate',
  /** New design only: user clicked a sortable column header — parent should reload from the
   *  backend with this orderby/order rather than the table re-sorting locally. */
  'update:sort',
  /** Ask the parent to fetch co-counsel expenses (mirrors Step 3's load-fee-recipients). */
  'load-co-counsel-expenses',
  /** User changed a coCounsel row's amount. referringAttorney rows never emit this — they have no input. */
  'update:coCounselExpense',
]);

/** Expense list toggle — collapsed by default */
const expenseListOpen = ref(false);

// ── Co-Counsel Expenses (P5.2) ─────────────────────────────
const coCounselSectionOpen = ref(false);

/** Auto-expand once if any row already has a confirmed/entered amount — otherwise a
 *  user returning to Step 2 sees the collapsed "Need to add..." link hiding data they
 *  already saved. Runs once (immediate covers data already in the store; the watcher
 *  covers it arriving async via load-co-counsel-expenses) and never fights a user who
 *  deliberately hits "Hide" afterward. */
let coCounselSectionAutoOpened = false;
watch(
  () => props.coCounselExpenses,
  (rows) => {
    if (!coCounselSectionAutoOpened && rows.some((e) => e.amount > 0)) {
      coCounselSectionOpen.value = true;
      coCounselSectionAutoOpened = true;
    }
  },
  { immediate: true },
);

/** Visible only when the flag is on and there's at least one matter recipient —
 *  the co-counsel endpoint already returns one row per recipient (joined with any
 *  confirmed amount), so this list's length doubles as the recipient-count check. */
const showCoCounselSection = computed(
  () => props.coCounselEnabled && props.coCounselExpenses.length > 0,
);

/** Reuses the existing expenseArrangement prop's friendly 'Absorbed' value — the
 *  FE-mapped form of the backend's FirmAbsorbs arrangement (see types.ts). */
const isFirmAbsorbsArrangement = computed(() => props.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED);

/** FE-mapped form of the backend's ClientPays arrangement (see types.ts). */
const isClientPaysArrangement = computed(() => props.expenseArrangement === 'Client');

/** Total Case Costs = this batch's unbilled expenses + previously billed ones (sc-46645) —
 *  the same figure ConfirmExpensesAsync folds in and saves for ClientPays. Equals
 *  totalExpenses for every other arrangement, where billedExpensesTotal is always 0.
 *  Sum in integer cents to avoid float drift (both operands are already cent-rounded upstream). */
const totalCaseCosts = computed(() =>
  (Math.round(props.totalExpenses * 100) + Math.round(props.billedExpensesTotal * 100)) / 100);

const coCounselExpenseTotal = computed(() =>
  props.coCounselExpenses
    .filter((e) => e.role === 'coCounsel')
    .reduce((sum, e) => sum + (e.amount || 0), 0),
);

/** Bottom-line "Total Expenses" footer figure — the firm's own expenses plus
 *  whatever co-counsel expenses have been entered (0 when the section isn't
 *  shown or has no rows yet). The card-header total stays firm-only since its
 *  "(N)" count matches only the itemized table rows. */
const totalExpensesWithCoCounsel = computed(() => props.totalExpenses + coCounselExpenseTotal.value);

function onCoCounselAmountChange(vendorId: string, value: string | number) {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  emit('update:coCounselExpense', { vendorId, amount: Number.isFinite(parsed) ? parsed : 0 });
}

onMounted(() => {
  if (props.redesignEnabled && props.coCounselEnabled && props.coCounselExpenses.length === 0) {
    emit('load-co-counsel-expenses');
  }
});

/** Sort state for new-design table columns (Date, Description, Amount). Defaults to Date ascending. */
const sortKey = ref<'date' | 'description' | 'amount' | null>('date');
const sortDir = ref<'asc' | 'desc'>('asc');

function toggleSort(key: 'date' | 'description' | 'amount') {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
  currentPage.value = 1;
  // Order is now backend-driven — ask the parent to reload rather than re-sorting locally.
  emit('update:sort', { orderby: key, order: sortDir.value });
}

/** True when the interest input has changed since the last confirmation — re-confirm needed. */
const interestNeedsReconfirm = computed(
  () =>
    !props.interestConfirmed ||
    props.expenseFromDate !== props.confirmedExpenseFromDate ||
    props.totalInterest !== props.confirmedTotalInterest,
);

/** Interest section toggle — open by default until expenses/interest are confirmed, then collapsed. */
const interestOpen = ref(interestNeedsReconfirm.value);

/** Date model for the old-design inline date picker. */
const expenseFromDateModel = computed<Date | null>(() => parseLocalDate(props.expenseFromDate));

function onExpenseFromDateChange(val: Date | null) {
  const next = yyyymmdddash(val) || null;
  if (next === props.expenseFromDate) return;
  emit('update:expenseFromDate', next);
}

/** Show the interest column + date picker when: rate set, matter hasn't opted out, and firm advances all expenses (BR-V2-33) */
const showInterest = computed(
  () =>
    (props.companyExpenseInterestRate ?? 0) > 0 &&
    !props.noExpenseInterest &&
    props.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM,
);


const hasConfirmedActiveInterest = computed(
  () =>
    showInterest.value &&
    props.interestConfirmed &&
    props.confirmedTotalInterest > 0 &&
    !interestNeedsReconfirm.value,
);

/** Page size: 6 rows in redesign, 15 in old design */
const pageSize = computed(() => (props.redesignEnabled ? 6 : 15));

/** Confirm button label — old design only. Shows Total Case Costs (unbilled + previously
 *  billed) so it matches what actually gets saved — equals totalExpenses for every
 *  non-ClientPays arrangement, where billedExpensesTotal is always 0 (sc-46645). */
const confirmButtonLabel = computed(() => {
  if (showInterest.value && props.totalInterest > 0) {
    return `${formatCurrency(totalCaseCosts.value)} + ${formatCurrency(props.totalInterest)} interest`;
  }
  return formatCurrency(totalCaseCosts.value);
});

/** "MM/dd/yyyy" display for use in the tfoot "through {date}" label */
const expenseFromDateDisplay = computed(() => {
  const d = parseLocalDate(props.expenseFromDate);
  return d ? format(d, 'MM/dd/yyyy') : '';
});

const currentPage = ref(1);

// Exclude the interest soft-cost from the table — it is shown below the table as a card.
// ClientPays only: merge in previously-billed case costs (tagged billed: true) so the Status
// column can show the whole picture in one table, not just two disconnected totals — billed
// rows are appended after unbilled, not date-interleaved (sc-46645).
const tableExpenses = computed(() => {
  const unbilled = props.expenses
    .filter((e) => !e.isExpenseInterest)
    .map((e) => ({ ...e, billed: false }));
  if (!isClientPaysArrangement.value || props.billedExpenses.length === 0) return unbilled;
  const billed = props.billedExpenses
    .filter((e) => !e.isExpenseInterest)
    .map((e) => ({ ...e, billed: true }));
  return [...unbilled, ...billed];
});

const totalPages = computed(() => Math.ceil(tableExpenses.value.length / pageSize.value));

// Clamp the page used for slicing so a shrinking list (e.g. after edits) doesn't leave
// currentPage past the end and render an empty page.
const effectivePage = computed(() => Math.min(currentPage.value, totalPages.value || 1));

const paginatedExpenses = computed(() => {
  const start = (effectivePage.value - 1) * pageSize.value;
  // tableExpenses is already in backend-sorted order (see update:sort) — just paginate it.
  return tableExpenses.value.slice(start, start + pageSize.value);
});

/** "Showing X–Y of Z" display values */
const paginationStart = computed(() => (effectivePage.value - 1) * pageSize.value + 1);
const paginationEnd = computed(() =>
  Math.min(effectivePage.value * pageSize.value, tableExpenses.value.length),
);

const noDateCount = computed(() =>
  tableExpenses.value.filter((e) => !e.date).length,
);

// Expenses dated strictly after the as-of date accrue 0 days of interest.
const afterAsOfCount = computed(() => {
  if (!props.expenseFromDate) return 0;
  return tableExpenses.value.filter(
    (e) => e.date && daysBetween(props.expenseFromDate as string, e.date) > 0,
  ).length;
});

function daysForExpense(expense: MatterExpenseItem): number {
  if (!props.expenseFromDate || !expense.date) return 0;
  return daysBetween(expense.date, props.expenseFromDate);
}

function interestForExpense(expense: MatterExpenseItem): number {
  if (!props.expenseFromDate || !expense.date || !props.companyExpenseInterestRate) return 0;
  const days = daysForExpense(expense);
  return computeExpenseInterest(expense.amount, props.companyExpenseInterestRate, days);
}

const { exportCSV, formatDate } = useExportUtilities();

/** Export the currently-loaded (backend-sorted) expense list — mirrors exactly what's shown in
 *  the table, not just the current page. Columns match the table's visible columns only. */
function exportExpensesCsv() {
  const columnMap: Record<string, string | ((expense: MatterExpenseItem & { billed?: boolean }) => unknown)> = {
    Date: (expense) => formatDate(expense.date),
    Description: 'description',
    ...(props.showVendor
      ? {
        Vendor: (expense) =>
          expense.vendor?.displayName || expense.vendor?.name || '',
      }
      : {}),
    ...(isClientPaysArrangement.value
      ? { Status: (expense) => (expense.billed ? 'Billed' : 'Unbilled') }
      : {}),
    Amount: (expense) => formatCurrency(expense.amount),
    ...(showInterest.value
      ? {
        Interest: (expense) =>
          expense.date && props.expenseFromDate ? formatCurrency(interestForExpense(expense)) : '',
      }
      : {}),
  };
  exportCSV(tableExpenses.value, columnMap, {
    filename: `settlement-expenses-${yyyymmdddash(new Date())}`,
  });
}
</script>

<style scoped>
.expense-list-card {
  border: 1px solid #e8eaed;
  border-radius: 8px;
  overflow: hidden;
}

.expense-list-head {
  background: #f7f8fa;
  border-bottom: 1px solid #e8eaed;
  user-select: none;
}

.expense-list-head:hover {
  background: #f0f2f5;
}

.expense-list-chevron {
  transition: transform 0.2s ease;
  color: #667085;
  flex-shrink: 0;
}

.expense-list-chevron--open {
  transform: rotate(90deg);
}

.expense-list-table :deep(thead th) {
  background: #fafbfc;
}

.expense-col-sortable {
  cursor: pointer;
  user-select: none;
}

.expense-col-sortable:hover {
  background: #f0f2f5;
}

.expense-sort-icon {
  opacity: 0.25;
  vertical-align: middle;
  margin-left: 2px;
}

.expense-sort-icon--active {
  opacity: 1;
}

/* Clickable rows (flag on) */
.expense-row--clickable {
  cursor: pointer;
  transition: background 0.1s ease;
}
.expense-row--clickable:hover {
  background: #f0fdf4;
}

.expense-page-indicator {
  min-width: 32px;
  text-align: center;
}

.interest-section {
  padding: 8px 12px;
  background: #f7f8fa;
  border: 1px solid #e8eaed;
  border-radius: 8px;
}


/* Totals footer — nested inside expense-list-card, no border/radius of its own */
.expense-list-foot {
  border-top: 1px solid #e8eaed;
}
.expense-totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #f7f8fa;
  border-bottom: 1px solid #e8eaed;
}
.expense-totals-row:last-child {
  border-bottom: none;
}
.expense-totals-row--total {
  background: #f0f2f5;
}
</style>
